import React, { useContext, useEffect, useState, useRef } from 'react';
// TODO Add non-filled checkmark icon (so just the actual checkmark) to DS and use here
import CheckIcon from 'mdi-react/CheckIcon';
import { AppContext } from '@prisjakt/ui-app-context';
import { PriceListFilterContext } from 'src/areas/product/price-list-filter-context';
import { ViewportContext } from '../../../../../components/viewport';
import { IconChevronUp } from '@prisjakt/ui/icon';
import { ButtonText, ButtonType } from '@prisjakt/ui/button/variants';
import {
  VariantGroup as VariantGroupType,
  Variant as VariantType,
} from 'src/types';
import {
  Color,
  SelectableButton,
  ShowMoreLessButton,
  ShowMoreLessInner,
  Tooltip,
  VariantGroup,
  VariantWrapper,
  WrapLine,
} from '../styles';

export const LegacyVariantGroup: React.FC<GroupProps> = ({
  group,
  handleSelectedVariant,
}) => {
  const { t } = useContext(AppContext);
  const { isDesktop } = useContext(ViewportContext);
  const [isDesktopContent, setIsDesktopContent] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [maxVisibleVariants, setMaxVisibleVariants] = useState<number>(0);
  const instanceId = useRef(Math.random()); // Since this is used inside media queries, we get aria failiures on non-unique keys without this
  const containerRef = useRef<HTMLDivElement>(null);
  const { selectedLegacyVariants } = useContext(PriceListFilterContext);

  useEffect(() => {
    setIsDesktopContent(Boolean(isDesktop));
  }, [isDesktop]);

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }
    const wrapper = containerRef.current.getBoundingClientRect().width;
    const variantWidth = 48;
    const max = Math.floor((wrapper - variantWidth) / variantWidth);

    setMaxVisibleVariants(group.variants?.length === max ? max : max - 1);
  }, [group.variants]);

  const selectedVariantId = selectedLegacyVariants?.length
    ? selectedLegacyVariants[0]
    : 0;

  if (!group.variants?.length) return null;

  const getVariantOrder = (index: number) => {
    let order = index + 1;
    if (order > maxVisibleVariants) {
      order += 2; // add room for showmore button
    }
    return order;
  };

  const capitalizeString = (s: string | null | undefined) =>
    s?.length && s[0].toUpperCase() + s.slice(1);

  const renderVariant = (variant: VariantType, index: number, key: string) => {
    const isSelected = variant.id === selectedVariantId;
    const isAll = !variant.data;

    return (
      <SelectableButton
        onClick={() => handleSelectedVariant(group, variant)}
        $order={getVariantOrder(index)}
        selected={variant.id === selectedVariantId}
        ariaLabel={`${group.name} - ${variant.name}`}
        type={ButtonType.TONAL}
        key={key}
      >
        {isAll ? (
          <ButtonText>{capitalizeString(variant.name)}</ButtonText>
        ) : null}
        {group.type === 'color' && variant.data ? (
          <Color $variantData={variant.data}>
            {isSelected ? <CheckIcon /> : null}
          </Color>
        ) : null}
      </SelectableButton>
    );
  };

  const renderAllVariants = (variants: VariantType[]) => {
    return variants.map((variant, index) => {
      const key = `${group.type}-${variant.id}-${instanceId.current}`;
      const tooltip = `${capitalizeString(variant.name)} (${variant.count})`;

      return (
        <Tooltip key={key} title={tooltip} order={getVariantOrder(index)}>
          {renderVariant(variant, index, key)}
        </Tooltip>
      );
    });
  };

  return (
    <VariantWrapper ref={containerRef}>
      {isDesktopContent ? (
        <VariantGroup data-test="variant-group">
          {renderAllVariants(
            group.variants
              .sort((a, b) => a.id - b.id)
              .filter(
                (_variant, index) => showAll || index < maxVisibleVariants
              )
          )}

          {group.variants.length > maxVisibleVariants ? (
            <>
              <ShowMoreLessButton
                key="show-more-less"
                selected={false}
                onClick={() => setShowAll(!showAll)}
                $order={maxVisibleVariants}
                ariaLabel={
                  showAll
                    ? t('common', 'button.show-less')
                    : t('common', 'button.show-more')
                }
                type={ButtonType.TONAL}
              >
                <ShowMoreLessInner>
                  {showAll ? (
                    <IconChevronUp color="maybeBlue700" />
                  ) : (
                    `+${group.variants.length - maxVisibleVariants}`
                  )}
                </ShowMoreLessInner>
              </ShowMoreLessButton>
              <WrapLine order={maxVisibleVariants + 1} />
            </>
          ) : null}
        </VariantGroup>
      ) : (
        <VariantGroup>
          {renderAllVariants(group.variants.sort((a, b) => a.id - b.id))}
        </VariantGroup>
      )}
    </VariantWrapper>
  );
};

interface GroupProps {
  group: VariantGroupType;
  handleSelectedVariant: (
    group: VariantGroupType,
    variant: VariantType | null
  ) => void;
}
